import type { Bill, BillingSummary } from "@/schemas/billing";

type Orientacion = {
  /**
   * - `cancelada`: sin recontratación desde acá; se gestiona en Labs.
   * - `recontratar`: el dueño puede volver a contratar desde Mi Suscripción
   *   (trd-client#336).
   * - `esperando-factura` y `habilitando`: los pasos de una recontratación en
   *   curso, antes y después de pagar la primera factura.
   * - `revision` y `soporte`: un pago en revisión, o un estado sin camino desde
   *   acá.
   */
  tipo:
    | "cancelada"
    | "recontratar"
    | "esperando-factura"
    | "habilitando"
    | "revision"
    | "soporte";
  titulo: string;
  detalle: string;
};
export type Recuperacion =
  | Orientacion
  | { tipo: "al-dia" }
  | { tipo: "pagar"; bill: Bill };

/**
 * Traduce el read model; nunca calcula mora ni concede acceso.
 *
 * `recovery.action` es la guía de octopus (v1.52.0, trd-octopus#380). Después
 * de recontratar, la suscripción queda en `pending_first_payment` y la acción
 * avanza: `wait_document` → `pay_bill` (o `wait_review`) → `wait_activation`.
 * Recién cuando la sesión se renueva sin `suspended` el acceso está habilitado:
 * eso no lo dice esta lectura, lo dice el token.
 */
export function recuperacion(resumen: BillingSummary): Recuperacion {
  const accion = resumen.recovery?.action;
  const pendiente = resumen.open_bill;
  const soporte: Orientacion = {
    tipo: "soporte",
    titulo: resumen.suspended
      ? "Tu negocio está suspendido"
      : "Tu suscripción necesita atención",
    detalle:
      "No podemos ofrecer un pago desde este estado. Contactá al administrador de tu organización para revisar la suscripción. Actualizar el estado no habilita el negocio por sí solo.",
  };
  const revision: Orientacion = {
    tipo: "revision",
    titulo: "Tu pago está en revisión",
    detalle:
      "La organización está revisando el pago informado. No vuelvas a pagar este documento. Cuando termine la revisión, actualizá el estado para ver el próximo paso.",
  };
  const pagable =
    pendiente?.status === "open" && resumen.recovery?.bill_id === pendiente.id;

  if (resumen.status === "canceled") {
    if (accion === "recontract")
      return {
        tipo: "recontratar",
        titulo: "Tu suscripción está cancelada",
        detalle:
          "Para volver a usar el servicio, elegí un plan y confirmá la nueva contratación. Pagar una deuda anterior no reactiva una suscripción cancelada.",
      };
    return {
      tipo: "cancelada",
      titulo: "Tu suscripción está cancelada",
      detalle:
        "Contactá al administrador de tu organización para revisar la deuda y gestionar la recuperación desde Labs. Pagar una deuda anterior no reactiva una suscripción cancelada.",
    };
  }
  // Una recontratación esperando su primer pago. Cualquier otra acción —el
  // documento anulado, una desconocida— no tiene camino desde acá.
  if (resumen.status === "pending_first_payment") {
    if (accion === "wait_document")
      return {
        tipo: "esperando-factura",
        titulo: "Estamos preparando tu factura",
        detalle:
          "La nueva contratación quedó registrada. En unos minutos vas a poder pagar la primera factura desde acá. Actualizá el estado para verla.",
      };
    if (accion === "pay_bill" && pagable && pendiente)
      return { tipo: "pagar", bill: pendiente };
    if (accion === "wait_review") return revision;
    if (accion === "wait_activation")
      return {
        tipo: "habilitando",
        titulo: "Recibimos el pago",
        detalle:
          "Estamos habilitando tu acceso. Cuando esté listo, la sesión se actualiza sola en el próximo paso que des; si no, volvé a iniciar sesión.",
      };
    return soporte;
  }
  if (!["active", "past_due"].includes(resumen.status)) return soporte;
  if (
    accion === "wait_review" ||
    (!accion && pendiente?.status === "under_review")
  ) {
    return revision;
  }
  if (accion && accion !== "none" && accion !== "pay_bill") return soporte;
  if (
    pendiente?.status === "open" &&
    (!accion || (accion === "pay_bill" && pagable))
  ) {
    return { tipo: "pagar", bill: pendiente };
  }
  if (
    accion === "pay_bill" ||
    pendiente ||
    resumen.suspended ||
    resumen.status !== "active" ||
    (resumen.dunning && resumen.dunning.step !== "current") ||
    !/^0+(?:\.0+)?$/.test(resumen.balance?.amount ?? "0")
  )
    return soporte;
  return { tipo: "al-dia" };
}
